import React from "react"
import { Link, graphql, useStaticQuery } from "gatsby"
import styled from "styled-components/macro"
import colors from "../scripts/colors"
import PageLayout from "../components/PageLayout"
import PageTitle from "../components/PageTitle/PageTitle"
import { PageMargin } from "../GlobalStyles"
import { camelCase } from "camel-case"
import ArticleNewsItem from "../components/News/ArchiveNewsItem"
import useFacilityData from "../../hooks/useFacilityData.ts"
import { Helmet } from "react-helmet"
import Button from "../components/Button/Button"
import MoreInfo from "../components/MoreInfo/MoreInfo"
import MoreInfoContact from "../components/MoreInfo/MoreInfo"
import { FaArrowRight, FaArrowLeft } from "react-icons/fa"

const ArchiveLayout = styled.section`
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  flex-direction: column;
  width: 100%;
  @media screen and (min-width: 1024px) {
    flex-direction: row;
  }
`

const ArticleMoreInfo = styled.section`
  .MoreInfo-content {
    .Button {
      margin-bottom: 50px;
      @media screen and (min-width: 790px) {
        margin-bottom: 0;
        &:first-child {
          margin-right: 50px;
        }
        &:only-child {
          margin-right: 0;
        }
      }
    }
  }
`

const ArticleArchive = ({ pageContext, data }) => {
  const currFacilityName = data.sanityFacility.facilityObj.facilityName
  // console.log(currFacilityName)

  const articleArr = data.allSanityArticle.edges.filter(edge => {
    const articleFacilityArr = edge.node.articleObj.facilities
    const allFacilities = edge.node.articleObj.allFacilities

    const facilityNameArr = []
    articleFacilityArr.forEach(facility => {
      facilityNameArr.push(facility.facilityObj.facilityName)
    })

    return facilityNameArr.includes(currFacilityName) || allFacilities
  })

  // console.log(articleArr)

  const articleList = articleArr.map(edge => {
    const articleObj = edge.node.articleObj
    return (
      <ArticleNewsItem
        key={edge.node.id}
        id={edge.node.id}
        articleData={articleObj}
      />
    )
  })

  const metaTitle = `News < ${currFacilityName}`

  const siteUrl = data.site.siteMetadata.siteUrl

  const url = `${siteUrl}/news`

  const { previousPagePath, nextPagePath } = pageContext

  return (
    <PageLayout currLocation={"/news"}>
      <Helmet>
        <title>{metaTitle}</title>
        <meta name="title" content={metaTitle} />
        <link rel="canonical" href={url} />

        <meta property="og:url" content={url} />
        <meta property="og:title" content={metaTitle} />

        <meta property="twitter:url" content={url} />
        <meta property="twitter:title" content={metaTitle} />

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              {
                "@type": "ListItem",
                position: 1,
                item: {
                  "@id": siteUrl,
                  name: "Home",
                },
              },
              {
                "@type": "ListItem",
                position: 2,
                item: {
                  "@id": `${siteUrl}/covid
                  )}`,
                  name: "News",
                },
              },
            ],
          })}
        </script>
      </Helmet>
      <PageMargin>
        <PageTitle>News</PageTitle>
        <ArchiveLayout>{articleList}</ArchiveLayout>
        <ArticleMoreInfo>
          <MoreInfo title="Browse">
            {previousPagePath && (
              <Button
                key="Next Articles"
                size="m"
                side="lt"
                element={Link}
                to={previousPagePath}
                icon={<FaArrowLeft />}
              >
                Next Articles
              </Button>
            )}
            {nextPagePath && (
              <Button
                key="Previous Articles"
                size="m"
                side="rt"
                element={Link}
                to={nextPagePath}
                icon={<FaArrowRight />}
              >
                Previous Articles
              </Button>
            )}
          </MoreInfo>
        </ArticleMoreInfo>
      </PageMargin>
    </PageLayout>
  )
}

export default ArticleArchive

export const articleQuery = graphql`
  query ($skip: Int!, $limit: Int!) {
    site {
      siteMetadata {
        siteUrl
      }
    }
    sanityFacility(_id: { eq: "084b80fe-4b53-45f2-8926-aacca2993ca1" }) {
      facilityObj {
        facilityName
      }
    }
    allSanityArticle(skip: $skip, limit: $limit) {
      edges {
        node {
          id
          _createdAt
          _updatedAt
          articleObj {
            _key
            title
            date(formatString: "dddd, MMMM Do, YYYY")
            summary
            bannerImage {
              asset {
                source {
                  id
                }
              }
            }
            metaTitle
            metaDescription
            canonicalUrl
            _rawContent
            allFacilities
            facilities {
              facilityObj {
                facilityName
              }
            }
          }
        }
      }
    }
  }
`
